import React, { useEffect, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Users, Building2, FileText, Star, ChevronDown } from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import * as XLSX from 'xlsx';
import baseurl from '../Baseurl/baseurl';
import { useCustomTheme } from '../../context/ThemeContext';
import { Card, CardTitle, Stat, Eyebrow, Button } from '../ui';

const Dashboard = () => {
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const [totalMembers, setTotalMembers] = useState(0);
  const [activeBusinesses, setActiveBusinesses] = useState(0);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [reviewsThisMonth, setReviewsThisMonth] = useState(0);
  const [, setReviews] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const dark = mode === 'dark';
  const C = {
    green: dark ? '#29F5A6' : '#06C281',
    teal: dark ? '#34E0E0' : '#11A6B6',
    violet: dark ? '#9B8CFF' : '#6C5CE7',
    grid: dark ? 'rgba(255,255,255,0.06)' : 'rgba(12,42,34,0.06)',
    axis: dark ? '#6C8595' : '#6E8C82',
    tipBg: dark ? '#122030' : '#ffffff',
    tipBorder: dark ? 'rgba(120,230,190,0.18)' : 'rgba(11,174,126,0.22)',
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await fetch(`${baseurl}/api/member/all`);
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          const members = result.data;
          setTotalMembers(members.length);
          setActiveBusinesses(members.reduce((acc, m) => acc + (m.BusinessProfiles ? m.BusinessProfiles.length : 0), 0));
          setPendingApplications(members.filter((m) => m.status === 'Pending').length);
          const monthly = Array(12).fill(null).map((_, i) => ({
            name: new Date(0, i).toLocaleString('default', { month: 'short' }),
            'New Registrations': 0, 'Business Listings': 0,
          }));
          members.forEach((m) => {
            const mi = new Date(m.createdAt).getMonth();
            monthly[mi]['New Registrations'] += 1;
            if (Array.isArray(m.BusinessProfiles)) monthly[mi]['Business Listings'] += m.BusinessProfiles.length;
          });
          setChartData(monthly);
        }
      } catch (e) { console.error('Error fetching members:', e); }
    };
    const fetchReviews = async () => {
      try {
        const res = await fetch(`${baseurl}/api/ratings/all`);
        const result = await res.json();
        if (result?.data) {
          setReviews(result.data);
          const cm = new Date().getMonth(), cy = new Date().getFullYear();
          setReviewsThisMonth(result.data.filter((r) => { const d = new Date(r.createdAt); return d.getMonth() === cm && d.getFullYear() === cy; }).length);
        }
      } catch (e) { console.error('Error fetching reviews:', e); }
    };
    fetchMembers();
    fetchReviews();
  }, []);

  const exportToImage = async (format = 'png') => {
    const el = document.querySelector('.recharts-wrapper');
    if (!el) return;
    try {
      const url = format === 'png' ? await htmlToImage.toPng(el) : await htmlToImage.toJpeg(el, { quality: 0.95 });
      const a = document.createElement('a'); a.download = `growth-analytics.${format}`; a.href = url; a.click();
    } catch (e) { console.error(e); }
  };
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(chartData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Growth Analytics');
    XLSX.writeFile(wb, 'growth-analytics.xlsx');
  };
  const closeMenu = () => setAnchorEl(null);

  // Values are derived from the API. No month-over-month deltas are shown
  // because the API returns no historical data to compute them from.
  const stats = [
    { title: 'Total Members', value: totalMembers.toLocaleString(), icon: <Users size={18} />, onClick: () => navigate('/admin/MemberManagement?tab=all') },
    { title: 'Active Businesses', value: activeBusinesses.toLocaleString(), icon: <Building2 size={18} />, onClick: () => navigate('/admin/BusinessManagement') },
    { title: 'Pending Applications', value: pendingApplications.toLocaleString(), icon: <FileText size={18} />, onClick: () => navigate('/admin/MemberManagement?tab=pending') },
    { title: 'Reviews This Month', value: reviewsThisMonth.toLocaleString(), icon: <Star size={18} />, onClick: () => navigate('/admin/ReviewTestimonals') },
  ];

  const tip = { backgroundColor: C.tipBg, border: `1px solid ${C.tipBorder}`, borderRadius: 12, boxShadow: 'var(--shadow-card)', color: 'var(--text-c)' };
  const tick = { fontSize: 11, fill: C.axis };

  return (
    <div className="space-y-6">
      {/* Page head */}
      <div>
        <Eyebrow>Overview</Eyebrow>
        <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">Community Dashboard</h1>
        <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">A live snapshot of members, businesses and reviews across the network.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.title} interactive padding="md" onClick={s.onClick} className="cursor-pointer">
            <Stat value={s.value} label={s.title} icon={s.icon} />
          </Card>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 gap-4">
        <Card padding="none">
          <div className="flex items-center justify-between px-6 pb-2 pt-6">
            <div className="flex flex-col gap-1">
              <CardTitle>Growth Analytics</CardTitle>
              <span className="text-xs text-muted">Monthly new registrations &amp; business listings</span>
            </div>
            <Button variant="subtle" size="sm" rightIcon={<ChevronDown size={15} />} onClick={(e) => setAnchorEl(e.currentTarget)}>Export</Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
              <MenuItem onClick={() => { exportToExcel(); closeMenu(); }}>Export to Excel</MenuItem>
              <MenuItem onClick={() => { exportToImage('png'); closeMenu(); }}>Export as PNG</MenuItem>
              <MenuItem onClick={() => { exportToImage('jpeg'); closeMenu(); }}>Export as JPEG</MenuItem>
            </Menu>
          </div>
          <div className="h-[300px] px-4 pb-5">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={tick} />
                <YAxis axisLine={false} tickLine={false} tick={tick} />
                <Tooltip contentStyle={tip} cursor={{ fill: C.grid }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="New Registrations" fill={C.green} radius={[5, 5, 0, 0]} />
                <Bar dataKey="Business Listings" fill={C.teal} radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

      </div>

      {/* Two charts were removed here because both rendered hardcoded
          placeholders as if they were real data:
            · "Top Active Users"  — a fictional cast (John Doe, Jane Smith, …)
            · "Search & App Hits" — US cities (New York, Chicago, …) for a
                                    Salem, Tamil Nadu community
          The API exposes no engagement or per-city search metric to derive
          either from. "Growth Analytics" above is kept: it is computed from
          real member createdAt dates. */}
    </div>
  );
};

export default Dashboard;
